import { useEffect, useState } from "react";
import { usDate } from "@/lib/dates";
import {
  BellRing, Save, Loader2, MessageSquareText, Mail, PlayCircle,
  CheckCircle2, XCircle, MinusCircle, Clock, RefreshCw, Search,
} from "lucide-react";
import { LoadMoreBar, PAGE_SIZE as SERVER_PAGE, totalFrom } from "@/components/LoadMoreBar";
import api, { formatApiErrorDetail } from "@/lib/api";
import { AbsenceLetterEditor, TardyLetterEditor } from "@/components/AbsenceLetterEditor";
import { toast } from "sonner";

const TIMEZONES = [
  ["America/New_York", "Eastern (New York)"],
  ["America/Chicago", "Central (Chicago)"],
  ["America/Denver", "Mountain (Denver)"],
  ["America/Phoenix", "Arizona (Phoenix)"],
  ["America/Los_Angeles", "Pacific (Los Angeles)"],
  ["America/Anchorage", "Alaska"],
  ["Pacific/Honolulu", "Hawaii"],
  ["UTC", "UTC"],
];

export default function NotificationsPage() {
  const [form, setForm] = useState({
    absence_enabled: false, tardy_enabled: false, bus_enabled: true,
    sms_enabled: false, email_enabled: true,
    absence_time: "15:30", timezone: "America/New_York",
    absence_letter: "", absence_phone: "", tardy_letter: "",
  });
  const [status, setStatus] = useState({ sms_configured: false, email_configured: false, absence_letter_default: "", tardy_letter_default: "", building_phone: "", school_name: "" });
  const [log, setLog] = useState([]);
  const [logTotal, setLogTotal] = useState(0);
  const [moreLoading, setMoreLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);
  const [lastRun, setLastRun] = useState(null);
  const [q, setQ] = useState("");
  const [typeFilter, setTypeFilter] = useState("");

  const nql = q.trim().toLowerCase();
  const visibleLog = log.filter((n) =>
    (!typeFilter || n.type === typeFilter) &&
    (!nql ||
      (n.student_name || "").toLowerCase().includes(nql) ||
      (n.recipient || "").toLowerCase().includes(nql) ||
      (n.recipient_name || "").toLowerCase().includes(nql))
  );

  const loadLog = async () => {
    try {
      const res = await api.get("/notifications", { params: { limit: SERVER_PAGE } });
      setLog(res.data); setLogTotal(totalFrom(res));
    } catch { /* silent */ }
  };

  useEffect(() => {
    (async () => {
      try {
        const [{ data }] = await Promise.all([api.get("/settings/notifications"), loadLog()]);
        setForm({
          absence_enabled: data.absence_enabled, tardy_enabled: data.tardy_enabled,
          bus_enabled: data.bus_enabled !== false,
          sms_enabled: data.sms_enabled, email_enabled: data.email_enabled,
          absence_time: data.absence_time || "15:30", timezone: data.timezone || "America/New_York",
          absence_letter: data.absence_letter || "", absence_phone: data.absence_phone || "", tardy_letter: data.tardy_letter || "",
        });
        setStatus({ sms_configured: data.sms_configured, email_configured: data.email_configured, absence_letter_default: data.absence_letter_default || "", tardy_letter_default: data.tardy_letter_default || "", building_phone: data.building_phone || "", school_name: data.school_name || "" });
      } catch (e) {
        toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load settings");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.put("/settings/notifications", form);
      setForm((f) => ({ ...f, absence_letter: data.absence_letter || "", absence_phone: data.absence_phone || "", tardy_letter: data.tardy_letter || "" }));
      setStatus((s) => ({ ...s, sms_configured: data.sms_configured, email_configured: data.email_configured }));
      toast.success("Notification settings saved");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const runNow = async () => {
    setRunning(true);
    try {
      const { data } = await api.post("/notifications/absence-check");
      setLastRun(data);
      if (data.skipped) toast.info(`Check skipped — today is a ${data.skipped}.`);
      else toast.success(`Checked ${data.students_checked} students · ${data.absent} absent · notified parents of ${data.students_notified}`);
      loadLog();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Absence check failed");
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="p-6 md:p-10" data-testid="notifications-page">
      <header className="mb-8">
        <div className="text-xs uppercase tracking-[0.25em] text-slate-500">Parent communication</div>
        <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900">Parent Alerts</h1>
        <p className="text-sm text-slate-600 mt-1">
          Automatically text or email parents when a student is absent or tardy. Contacts come from each student's authorized-pickup list.
        </p>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-[520px_1fr] gap-6">
        <form onSubmit={save} className="rounded-xl border border-slate-200 bg-white p-6 space-y-5 self-start" data-testid="notif-settings-card">
          <div className="flex items-center gap-2 text-slate-900 font-display font-semibold">
            <BellRing className="h-4 w-4 text-primary" /> Alert rules
          </div>

          <Toggle
            label="Absence alert (end of school day)"
            hint="Every school day, students with no drop-off or tardy check-in get an absence alert sent to their parents."
            checked={form.absence_enabled}
            onChange={(v) => setForm({ ...form, absence_enabled: v })}
            testid="notif-toggle-absence"
            disabled={loading}
          />
          {form.absence_enabled && (
            <div className="grid grid-cols-2 gap-3 pl-1">
              <label className="block">
                <span className="block mb-1 text-xs uppercase tracking-widest text-slate-500">Check time</span>
                <input
                  type="time" value={form.absence_time}
                  onChange={(e) => setForm({ ...form, absence_time: e.target.value })}
                  data-testid="notif-absence-time" className={inputCls} disabled={loading}
                />
              </label>
              <label className="block">
                <span className="block mb-1 text-xs uppercase tracking-widest text-slate-500">Timezone</span>
                <select
                  value={form.timezone}
                  onChange={(e) => setForm({ ...form, timezone: e.target.value })}
                  data-testid="notif-timezone" className={inputCls} disabled={loading}
                >
                  {TIMEZONES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </label>
            </div>
          )}

          <Toggle
            label="Tardy alert (instant)"
            hint="The moment the kiosk records a tardy arrival, parents get notified."
            checked={form.tardy_enabled}
            onChange={(v) => setForm({ ...form, tardy_enabled: v })}
            testid="notif-toggle-tardy"
            disabled={loading}
          />

          <Toggle
            label="Bus alerts (instant)"
            hint="The moment a child boards or gets off the school bus, parents get notified."
            checked={form.bus_enabled}
            onChange={(v) => setForm({ ...form, bus_enabled: v })}
            testid="notif-toggle-bus"
            disabled={loading}
          />

          <AbsenceLetterEditor
            letter={form.absence_letter} phone={form.absence_phone}
            defaultLetter={status.absence_letter_default} buildingPhone={status.building_phone} schoolName={status.school_name}
            onChange={(patch) => setForm((f) => ({ ...f, ...patch }))} disabled={loading}
          />
          <TardyLetterEditor
            letter={form.tardy_letter} phone={form.absence_phone}
            defaultLetter={status.tardy_letter_default} buildingPhone={status.building_phone} schoolName={status.school_name}
            onChange={(patch) => setForm((f) => ({ ...f, ...patch }))} disabled={loading}
          />

          <div className="border-t border-slate-100 pt-4 space-y-4">
            <div className="text-xs uppercase tracking-widest text-slate-500">Channels</div>
            <ChannelToggle
              Icon={Mail} label="Email" configured={status.email_configured}
              configHint="Add SENDGRID_API_KEY + SENDER_EMAIL to enable"
              checked={form.email_enabled}
              onChange={(v) => setForm({ ...form, email_enabled: v })}
              testid="notif-toggle-email"
              disabled={loading}
            />
            <ChannelToggle
              Icon={MessageSquareText} label="SMS text" configured={status.sms_configured}
              configHint="Add Twilio credentials to enable"
              checked={form.sms_enabled}
              onChange={(v) => setForm({ ...form, sms_enabled: v })}
              testid="notif-toggle-sms"
              disabled={loading}
            />
          </div>

          <div className="flex items-center gap-3 pt-2">
            <button
              type="submit" disabled={saving || loading} data-testid="notif-save-btn"
              className="inline-flex items-center gap-2 rounded-lg bg-primary px-5 py-2.5 text-sm font-medium text-white hover:shadow disabled:opacity-60 transition"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save
            </button>
            <button
              type="button" onClick={runNow} disabled={running || loading} data-testid="notif-run-now-btn"
              className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-100 disabled:opacity-60 transition"
            >
              {running ? <Loader2 className="h-4 w-4 animate-spin" /> : <PlayCircle className="h-4 w-4" />} Run absence check now
            </button>
          </div>
          {lastRun && !lastRun.skipped && (
            <div className="text-xs text-slate-500" data-testid="notif-last-run">
              Last check ({usDate(lastRun.date)}): {lastRun.students_checked} students · {lastRun.absent} absent · {lastRun.students_notified} newly notified
            </div>
          )}
        </form>

        <section className="rounded-xl border border-slate-200 bg-white overflow-hidden self-start" data-testid="notif-log-card">
          <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
            <div className="flex items-center gap-2 font-display font-semibold text-slate-900">
              <Clock className="h-4 w-4 text-primary" /> Recent alerts
            </div>
            <button onClick={loadLog} className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-900" data-testid="notif-log-refresh">
              <RefreshCw className="h-3.5 w-3.5" /> Refresh
            </button>
          </div>
          <div className="flex items-center gap-2 px-5 py-2.5 border-b border-slate-100 bg-slate-50/50">
            <div className="relative flex-1 max-w-xs">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                data-testid="notif-search-input"
                placeholder="Search student or recipient…"
                className="h-8 w-full rounded-lg border border-slate-200 bg-white pl-8 pr-3 text-xs focus:border-primary focus:ring-4 focus:ring-primary/15 outline-none"
              />
            </div>
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              data-testid="notif-type-filter"
              className="h-8 rounded-lg border border-slate-200 bg-white px-2 text-xs focus:border-primary outline-none"
            >
              <option value="">All types</option>
              <option value="absence">Absence</option>
              <option value="tardy">Tardy</option>
              <option value="bus_board">Bus board</option>
              <option value="bus_exit">Bus exit</option>
              <option value="bus_status">Bus status</option>
            </select>
            {(nql || typeFilter) && <span className="text-[11px] text-slate-500">{visibleLog.length} of {log.length}</span>}
          </div>
          <table className="w-full text-sm" data-testid="notif-log-table">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-slate-500 bg-slate-50">
                <th className="px-5 py-2.5">When</th>
                <th className="px-5 py-2.5">Student</th>
                <th className="px-5 py-2.5">Alert</th>
                <th className="px-5 py-2.5">To</th>
                <th className="px-5 py-2.5">Status</th>
              </tr>
            </thead>
            <tbody>
              {log.length === 0 && (
                <tr><td colSpan={5} className="px-5 py-10 text-center text-slate-500">
                  No alerts sent yet. Enable a rule, then run an absence check or record a tardy on the kiosk.
                </td></tr>
              )}
              {log.length > 0 && visibleLog.length === 0 && (
                <tr><td colSpan={5} className="px-5 py-10 text-center text-slate-500">No alerts match your search or filter.</td></tr>
              )}
              {visibleLog.map((n) => (
                <tr key={n.id} className="border-t border-slate-100" data-testid={`notif-row-${n.id}`}>
                  <td className="px-5 py-2.5 text-xs text-slate-500 whitespace-nowrap">{new Date(n.created_at).toLocaleString()}</td>
                  <td className="px-5 py-2.5 font-medium text-slate-900">{n.student_name}</td>
                  <td className="px-5 py-2.5">
                    <span className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-medium ${
                      n.type === "tardy" ? "bg-amber-100 text-amber-800"
                      : n.type === "bus_board" ? "bg-emerald-100 text-emerald-800"
                      : n.type === "bus_exit" ? "bg-blue-100 text-blue-800"
                      : n.type === "bus_status" ? "bg-violet-100 text-violet-800"
                      : "bg-rose-100 text-rose-800"}`}>
                      {n.type === "bus_board" ? "bus · on" : n.type === "bus_exit" ? "bus · off" : n.type === "bus_status" ? "bus · status" : n.type}
                    </span>
                  </td>
                  <td className="px-5 py-2.5 text-xs text-slate-700">
                    <div className="flex items-center gap-1.5">
                      {n.channel === "sms" ? <MessageSquareText className="h-3.5 w-3.5 text-slate-400" /> : <Mail className="h-3.5 w-3.5 text-slate-400" />}
                      <span>{n.recipient}{n.recipient_name ? ` (${n.recipient_name})` : ""}</span>
                    </div>
                  </td>
                  <td className="px-5 py-2.5">
                    <StatusBadge status={n.status} error={n.error} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <LoadMoreBar shown={log.length} total={logTotal} loading={moreLoading} testid="notif-load-more" onMore={async () => {
            setMoreLoading(true);
            try { const res = await api.get("/notifications", { params: { limit: SERVER_PAGE, offset: log.length } }); setLog((p) => [...p, ...res.data]); setLogTotal(totalFrom(res)); }
            finally { setMoreLoading(false); }
          }} />
        </section>
      </div>
    </div>
  );
}

function Toggle({ label, hint, checked, onChange, testid, disabled }) {
  return (
    <button
      type="button" onClick={() => onChange(!checked)} disabled={disabled} data-testid={testid}
      className="w-full flex items-start justify-between gap-4 text-left group"
    >
      <span>
        <span className="block text-sm font-medium text-slate-900">{label}</span>
        <span className="block text-xs text-slate-500 mt-0.5">{hint}</span>
      </span>
      <span className={`mt-0.5 relative inline-flex h-6 w-11 shrink-0 rounded-full transition ${checked ? "bg-primary" : "bg-slate-200"}`}>
        <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-all ${checked ? "left-[22px]" : "left-0.5"}`} />
      </span>
    </button>
  );
}

function ChannelToggle({ Icon, label, configured, configHint, checked, onChange, testid, disabled }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <div className="flex items-center gap-2.5">
        <Icon className="h-4 w-4 text-slate-500" />
        <span className="text-sm font-medium text-slate-900">{label}</span>
        {configured ? (
          <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-medium text-emerald-800">
            <CheckCircle2 className="h-3 w-3" /> Configured
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-500" title={configHint}>
            <MinusCircle className="h-3 w-3" /> Not configured
          </span>
        )}
      </div>
      <button
        type="button" onClick={() => onChange(!checked)} disabled={disabled} data-testid={testid}
        className={`relative inline-flex h-6 w-11 shrink-0 rounded-full transition ${checked ? "bg-primary" : "bg-slate-200"}`}
      >
        <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-all ${checked ? "left-[22px]" : "left-0.5"}`} />
      </button>
    </div>
  );
}

function StatusBadge({ status, error }) {
  if (status === "sent") {
    return <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-medium text-emerald-800"><CheckCircle2 className="h-3 w-3" /> Sent</span>;
  }
  if (status === "skipped") {
    return <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-500" title={error}><MinusCircle className="h-3 w-3" /> Not configured</span>;
  }
  return <span className="inline-flex items-center gap-1 rounded-full bg-rose-100 px-2 py-0.5 text-[11px] font-medium text-rose-800" title={error}><XCircle className="h-3 w-3" /> Failed</span>;
}

const inputCls = "w-full h-10 rounded-lg border border-slate-200 bg-white px-3 text-sm focus:border-primary focus:ring-4 focus:ring-primary/15 outline-none";
